import { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";
import { supabase } from "./supabaseClient";

const GHOST_ID = 13;


function CalendarPage() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [spots, setSpots] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [reservedBy, setReservedBy] = useState("");
  const [lastName, setLastName] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [previewPrice, setPreviewPrice] = useState(0);
  const [manualPrice, setManualPrice] = useState("");
  const [isLongTerm, setIsLongTerm] = useState(false);
  const [apartment, setApartment] = useState("");
  const [apartments, setApartments] = useState([]);
  const [apartmentId, setApartmentId] = useState(null);


  useEffect(() => {
    fetchSpots();
    fetchApartments();         // 👈 add this
}, []);

async function fetchApartments() {
  const { data, error } = await supabase
    .from("apartments")
    .select("id, name, active")
    .eq("active", true)
    .order("name", { ascending: true });
  if (!error) setApartments(data || []);
}

  useEffect(() => {
    fetchReservations();
  }, [startDate, endDate]);

  useEffect(() => {
    calculatePreviewPrice();
  }, [startDate, endDate, selectedSpot]);

  async function fetchSpots() {
  const { data, error } = await supabase
    .from("spots")
    .select("*")
    .neq("id", 13);

  // sort by the number inside the name, e.g. "SPOT 17 EV" -> 17
  const getNum = (s) => {
    const m = (s?.name || "").match(/\d+/);
    return m ? parseInt(m[0], 10) : s.id;
  };

  const sorted = (data || []).sort((a, b) => getNum(a) - getNum(b));
  setSpots(sorted);
}



  async function fetchReservations() {
  if (!startDate) return;

  const start = dayjs(startDate).format("YYYY-MM-DD");
  const end = endDate ? dayjs(endDate).format("YYYY-MM-DD") : start;

  const { data } = await supabase
    .from("reservations")
    .select("*")
    .lte("start_date", end)
    .gte("end_date", start)
    .neq("spot_id", GHOST_ID); // in case any slip back in

  setReservations(data || []);
}


  async function calculatePreviewPrice() {
    if (!startDate || !selectedSpot) {
      setPreviewPrice(0);
      return;
    }

    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = endDate ? dayjs(endDate).format("YYYY-MM-DD") : start;

    const { data: spotData } = await supabase
      .from("spots")
      .select("type")
      .eq("id", selectedSpot)
      .single();

    const spotType = spotData?.type || "normal";

    const { data: pricingData } = await supabase
      .from("pricing")
      .select("month, type, price_per_day");

    let total = 0;
    let current = dayjs(start);
    const endObj = dayjs(end);

    while (current.isBefore(endObj) || current.isSame(endObj, "day")) {
      const month = current.month() + 1;
      const priceRow = pricingData.find(
        (p) => p.month === month && p.type === spotType
      );
      total += priceRow ? priceRow.price_per_day : 0;
      current = current.add(1, "day");
    }

    setPreviewPrice(total);
  }

  async function addReservation() {
    if (!selectedSpot || !reservedBy || !lastName || !licensePlate || !startDate)
      return;

    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = endDate ? dayjs(endDate).format("YYYY-MM-DD") : start;

    const { data: spotData } = await supabase
      .from("spots")
      .select("type")
      .eq("id", selectedSpot)
      .single();

    const spotType = spotData?.type || "normal";

    let totalPrice = 0;

    if (manualPrice) {
      totalPrice = Number(manualPrice);
    } else {
      let current = dayjs(start);
      const endObj = dayjs(end);

      const { data: pricingData } = await supabase
        .from("pricing")
        .select("month, type, price_per_day");

      while (current.isBefore(endObj) || current.isSame(endObj, "day")) {
        const month = current.month() + 1;
        const priceRow = pricingData.find(
          (p) => p.month === month && p.type === spotType
        );
        totalPrice += priceRow ? priceRow.price_per_day : 0;
        current = current.add(1, "day");
      }
    }

    const { data: existing } = await supabase
      .from("reservations")
      .select("*")
      .eq("spot_id", selectedSpot)
      .lte("start_date", end)
      .gte("end_date", start);

    if (existing && existing.length > 0) {
      alert("This spot is already reserved for those dates!");
      return;
    }

    await supabase.from("reservations").insert([
      {
        spot_id: selectedSpot,
        start_date: start,
        end_date: end,
        reserved_by: reservedBy,
        last_name: lastName,
        license_plate: licensePlate,
        total_price: totalPrice,
        booking_type: isLongTerm ? "long-term" : "short-term",
        manual_override: !!manualPrice,
        apartment_id: apartmentId,           // 👈 new
      },
    ]);

    setReservedBy("");
    setLastName("");
    setLicensePlate("");
    setManualPrice("");
    setIsLongTerm(false);
    setSelectedSpot(null);
    fetchReservations();
  }

  const reservedSpotIds = reservations.map((r) => r.spot_id);

  return (
    <div style={{ padding: 20, fontFamily: "sans-serif", maxWidth: 800, margin: "auto" }}>
      <h1>Parking Spot Manager</h1>

      <Calendar
        onClickDay={(value) => {
          if (!startDate || (startDate && endDate)) {
            setStartDate(value);
            setEndDate(null);
          } else if (startDate && !endDate) {
            if (value < startDate) {
              setEndDate(startDate);
              setStartDate(value);
            } else {
              setEndDate(value);
            }
          }
        }}
        value={startDate && endDate ? [startDate, endDate] : startDate}
        selectRange={true}
      />

      <h2>
        {startDate && !endDate && dayjs(startDate).format("DD MMM YYYY")}
        {startDate && endDate &&
          `${dayjs(startDate).format("DD MMM YYYY")} - ${dayjs(endDate).format("DD MMM YYYY")}`}
      </h2>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginTop: 20,
          justifyContent: "center",
        }}
      >
        {spots.map((spot) => (
          <div
            key={spot.id}
            onClick={() => setSelectedSpot(spot.id)}
            style={{
              minWidth: 100,
              flex: "1 0 30%",
              maxWidth: 120,
              padding: 10,
              border: "1px solid #ccc",
              cursor: "pointer",
              backgroundColor: reservedSpotIds.includes(spot.id)
                ? "tomato"
                : "lightgreen",
              opacity: selectedSpot === spot.id ? 0.6 : 1,
              textAlign: "center",
              borderRadius: 4,
            }}
          >
            {spot.name}
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 30,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <select
  value={apartmentId || ""}
  onChange={(e) => setApartmentId(e.target.value ? Number(e.target.value) : null)}
>
  <option value="">Select apartment (optional)</option>
  {apartments.map((a) => (
    <option key={a.id} value={a.id}>
      {a.name}
    </option>
  ))}
</select>



        <input
          placeholder="First name"
          value={reservedBy}
          onChange={(e) => setReservedBy(e.target.value)}
        />
        <input
          placeholder="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <input
          placeholder="License plate"
          value={licensePlate}
          onChange={(e) => setLicensePlate(e.target.value)}
        />
        <input
          type="number"
          placeholder="Manual price (optional)"
          value={manualPrice}
          onChange={(e) => setManualPrice(e.target.value)}
        />

        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={isLongTerm}
            onChange={() => setIsLongTerm(!isLongTerm)}
          />
          Long-term booking
        </label>

        {previewPrice > 0 && (
          <div style={{ fontWeight: "bold" }}>Total Price: €{previewPrice}</div>
        )}

        <button onClick={addReservation}>Reserve</button>
      </div>
    </div>
  );
}

export default CalendarPage;
